import { useAuth } from "@hooks/useAuth";
import { useDataMutation } from "@hooks/useData/useDataMutation";
import useDialog from "@hooks/useDialog";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { videoToBase64 } from "@utils/imageToBase64";
import { uploadVideoToStream } from "@utils/uploadVideoToStream";
import { getPagination, selectData, softDelete, supabase } from "@utils/serverClient";
import { undefinedToNull } from "lib/utils";
import { useMemo, useState } from "react";
import { InsertVideo, UpdateVideo, Video } from "types";
import { z } from "zod";

const getPublicVideosSchema = z.object({
  paginate: z.boolean().optional(),
  searchQuery: z.string().optional(),
  from: z.number(),
  to: z.number(),
})

interface VideoUploadMetadata {
  name: string;
  extension: string;
  org_id: string;
  is_public?: boolean;
}

interface VideoUploadData {
  data: {
    metadata: VideoUploadMetadata;
    file: File;
  };
}

async function _uploadVideo(data: VideoUploadData): Promise<Video> {
  return uploadVideoToStream({
    file: data.data.file,
    metadata: data.data.metadata,
  });
}

export const _getPublicVideos = (async ({ paginate, searchQuery, from, to }: z.infer<typeof getPublicVideosSchema>) => {
  const { data, error } = paginate
    ? searchQuery
      ? await selectData({
        table: "video",
        columns: `*`,
      })
        .is("org_id", null)
        .order("created_at", { ascending: false })
        .range(from, to)
        .textSearch("name", searchQuery)
      : await selectData({
        table: "video",
        columns: `*`,
      })
        .is("org_id", null)
        .order("created_at", { ascending: false })
        .range(from, to)
    : await selectData({
      table: "video",
      columns: `*`,
    })
      .is("org_id", null)
      .order("created_at", { ascending: false });

  if (!data || error) throw error;

  return data;
});

export const _uploadVideoWithUrl = (async (video: InsertVideo) => {
  const { error, data } = await supabase.from("video").insert(video).select().single();

  if (error) throw new Error("Upload failed" + error?.message);

  return data;
})

export const _deleteVideo = (async (id: string) => {
  const { data, error } = await softDelete({
    table: "video",
  }).match({ id });
  if (error) throw new Error(error.message);
  return data;
});

export const _updateVideo = async (video: UpdateVideo) => {
  const { data, error } = await supabase.from("video").update(video).match({ id: video.id }).select().single();
  if (error) throw new Error(error.message);
  return data;
}

export const usePublicVideos = () => {
  const paginate = true
  const { showErrorSnackbar, showSuccessSnackbar } = useDialog();
  const [pageSize, setPageSize] = useState(30);
  const [searchQuery, setSearchQuery] = useState<string | undefined>(undefined);

  const queryKey = ["public_videos", searchQuery, paginate]

  const [uploadingImages, setUploadingImages] = useState<string[]>([]);

  const { organization } = useAuth()

  const { mutate: uploadAsset, isPending: isUploading } = useDataMutation({
    mutationFn: async (acceptedFiles: File[]) => {
      if (!organization?.id) return;
      if (acceptedFiles.length === 0) return;
      if (acceptedFiles.length > 1) {
        showErrorSnackbar("Please select only one video at a time.");
        return;
      }

      const acceptedFile = acceptedFiles[0];

      if (!acceptedFile) return;

      const src = URL.createObjectURL(acceptedFile);
      setUploadingImages([src]);

      const metadata = {
        name: acceptedFile.name.split(".")[0] ?? "",
        extension: acceptedFile.name.split(".")[1] ?? "",
        org_id: organization.id,
        is_public: true,
      };

      return _uploadVideo({
        data: {
          metadata,
          file: acceptedFile,
        },
      });
    },
    queryKeyToInvalidate: queryKey,
    errorMessage: "Failed to upload video",
    optimisticUpdateFn: (oldData: Video[] | undefined, files) => {
      return [...files.map<Video>(file => ({
        created_at: new Date().toISOString(),
        deleted: false,
        dimension: null,
        extension: file.name.split(".").pop() ?? "",
        id: "temp-" + Date.now(),
        name: file.name,
        org_id: organization?.id ?? "",
        tags: [],
        updated_at: null,
        url: URL.createObjectURL(file),
        thumbnail: null,
      })), ...(oldData ?? [])]
    },
  })

  const queryClient = useQueryClient();

  const {
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    data,
    status: VideosFetchStatus,
  } = useInfiniteQuery({
    queryKey,
    initialPageParam: 0,
    queryFn: async ({ pageParam = 0 }) => {
      if (pageParam === undefined) return;

      const { from, to } = getPagination(pageParam, pageSize);

      return _getPublicVideos({
        paginate,
        searchQuery,
        from,
        to,
      });
    },
    getNextPageParam: (lastGroup, groups) => (lastGroup ?? [])?.length < pageSize ? undefined : groups.length,
  });

  const { mutate: uploadAssetWithUrl } = useDataMutation({
    mutationFn: _uploadVideoWithUrl,
    queryKeyToInvalidate: queryKey,
    errorMessage: "Failed to upload video.",
    optimisticUpdateFn: (oldData, newVideo) => {
      return [...(oldData ?? []), undefinedToNull({ ...newVideo, id: 'temp-' + Date.now(), created_at: new Date().toISOString() })];
    },
  })

  const { mutate: deleteAsset } = useDataMutation({
    mutationFn: _deleteVideo,
    queryKeyToInvalidate: queryKey,
    errorMessage: "Failed to delete video.",
    optimisticUpdateFn: (oldData: Video[] | undefined, videoId) => {
      if (!oldData) return oldData;
      return oldData.filter((video) => video.id !== videoId);
    },
  })

  const { mutate: updateAsset, isPending: isUpdatingAsset } = useDataMutation({
    mutationFn: _updateVideo,
    queryKeyToInvalidate: queryKey,
    errorMessage: "Failed to update video.",
    optimisticUpdateFn: (oldData, video) => {
      if (!oldData) return oldData;
      return oldData.map((e) => (e.id === video.id ? undefinedToNull(video) : e));
    },
  })

  function getVideos() {
    queryClient.invalidateQueries({ queryKey });
  }

  const Videos = useMemo(() => {
    return data?.pages.flatMap((e) => e!) ?? [];
  }, [data])

  return {
    Videos,
    VideosFetchStatus,
    uploadAssetWithUrl,
    deleteAsset,
    updateAsset,
    isUpdatingAsset,
    getVideos,
    hasNextPage,
    pageSize,
    setPageSize,
    searchQuery,
    setSearchQuery,
    isFetchingNextPage,
    fetchNextPage,
    isUploading,
    uploadAsset,
    uploadingImages,
  }
};
